import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const isCod = params.get("cod") === "1";
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(isCod ? "success" : "checking");
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (isCod) { if (!cleared.current) { clear(); cleared.current = true; } return; }
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, isCod, clear]);

  return (
    <div data-testid="payment-success-page" className="pt-40 min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        {status === "checking" && (
          <><Loader2 className="w-12 h-12 text-mj-red animate-spin mx-auto mb-6" /><h1 className="font-serif-mj text-3xl">Confirming your payment…</h1></>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-mj-red mx-auto mb-6" strokeWidth={1.2} />
            <h1 className="font-serif-mj text-5xl tracking-tight text-mj-text">Thank you</h1>
            <p className="font-sans-mj text-mj-muted mt-5 leading-relaxed">
              {isCod ? "Your order is placed. We'll reach out to confirm delivery details. Pay in cash when your Manjaadii arrives." : "Your payment was successful and your order is confirmed. Your heritage is on its way."}
            </p>
            <Link to="/collection" className="inline-block mt-10 bg-mj-red text-mj-bg px-8 py-4 overline hover:bg-mj-reddark transition-colors">Continue Shopping</Link>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <h1 className="font-serif-mj text-4xl text-mj-text">{status === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="font-sans-mj text-mj-muted mt-5">{status === "timeout" ? "Your payment is taking longer than expected. Please check your email for confirmation." : "We couldn't confirm your payment. Please try again."}</p>
            <Link to="/checkout" className="inline-block mt-10 bg-mj-red text-mj-bg px-8 py-4 overline hover:bg-mj-reddark transition-colors">Back to Checkout</Link>
          </>
        )}
      </div>
    </div>
  );
}
